import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Checkbox from '@rio-cloud/rio-uikit/lib/es/Checkbox';
import Multiselect from '@rio-cloud/rio-uikit/lib/es/Multiselect';
import { validateHwSerial } from '~/features/base/utils/hwSerialValidator';
import DeviceTypeFormItem from '~/features/base/components/forms/DeviceTypeFormItem';
import BlockedReasonItem from '~/features/base/components/forms/BlockedReasonItem';
import Group from '~/prop-types/groupPropType';

const DeviceEditorForm = ({ model, groups, changeControlDeviceEditor }) => {
    const serialNumberInput = useRef(null);
    const descriptionInput = useRef(null);

    useEffect(() => {
        if (model.isNew) {
            serialNumberInput.current?.focus();
        } else {
            descriptionInput.current?.focus();
        }
    }, [model.isNew]);

    const handleSerialNumberChange = (e) => {
        const serialNumber = e.target.value;
        changeControlDeviceEditor({
            serialNumber,
            isValidSerialNumber: validateHwSerial(serialNumber),
        });
    };

    const handleDeviceTypeChange = (e) => {
        changeControlDeviceEditor({ type: e.deviceType });
    };

    const handleCheckboxChange = (field) => (e) => {
        changeControlDeviceEditor({ [field]: e.target.checked });
    };

    const handleUpdatesActiveChange = (e) => {
        changeControlDeviceEditor({
            updatesActive: !e.target.checked,
            updateBlockedReason: e.target.checked ? 'MANUAL' : undefined,
        });
    };

    const handleDeviceGroupsChange = (groupNames) => {
        changeControlDeviceEditor({ deviceGroups: groupNames });
    };

    const handleDescriptionChange = (e) => {
        changeControlDeviceEditor({ description: e.target.value });
    };

    const groupOptions = groups.map(group => ({
        id: group.name,
        label: group.name,
        selected: model.deviceGroups?.includes(group.name),
    }));

    return (
        <form>
            {model.isNew && (
                <div className='form-group form-group-serial-number'
                     validationState={model.isValidSerialNumber ? undefined : 'error'}>
                    <label className='control-label'>
                        <FormattedMessage id='intl-msg:serialNumber'/>
                    </label>
                    <div>
                        <FormattedMessage id='intl-msg:serialNumber.prompt'>
                            {placeholder =>
                                <input
                                    type='text'
                                    ref={serialNumberInput}
                                    className='form-control'
                                    value={model.serialNumber}
                                    placeholder={placeholder}
                                    onChange={handleSerialNumberChange}
                                />}
                        </FormattedMessage>
                    </div>
                    {!model.isValidSerialNumber && (
                        <span className='help-block'>
                            <FormattedMessage id='intl-msg:invalidSerialNumber'/>
                        </span>
                    )}
                </div>
            )}

            <div className='form-group form-group-device-type'>
                <DeviceTypeFormItem value={model.type} onChange={handleDeviceTypeChange}/>
            </div>

            <div className='form-group form-group-groups'>
                <label className='control-label'>
                    <FormattedMessage id='intl-msg:groups'/>
                </label>
                <div className='form-group'>
                    <Multiselect
                        placeholder={<FormattedMessage id='intl-msg:multiSelect.prompt'/>}
                        options={groupOptions}
                        onChange={handleDeviceGroupsChange}
                    />
                </div>
            </div>

            <div className='form-group form-group-in-customer-fleet'>
                <Checkbox onClick={handleCheckboxChange('inCustomerFleet')} checked={model.inCustomerFleet}>
                    <FormattedMessage id='intl-msg:inCustomerFleet'/>
                </Checkbox>
            </div>

            <div className='form-group form-group-updates-active'>
                <Checkbox onClick={handleUpdatesActiveChange} checked={!model.updatesActive}>
                    <FormattedMessage id='intl-msg:blocked'/>
                </Checkbox>
                {!model.updatesActive && (
                    <BlockedReasonItem blockedReason={model.updateBlockedReason} disabled />
                )}
            </div>

            <div className='form-group form-group-test-release-active'>
                <Checkbox onClick={handleCheckboxChange('testReleasesActive')} checked={model.testReleasesActive}>
                    <FormattedMessage id='intl-msg:testReleasesActive'/>
                </Checkbox>
            </div>

            <div className='form-group form-group-replaced'>
                <Checkbox onClick={handleCheckboxChange('replaced')} checked={model.replaced}>
                    <FormattedMessage id='intl-msg:replaced'/>
                </Checkbox>
            </div>

            <div className='form-group form-group-production-mode'>
                <Checkbox onClick={handleCheckboxChange('productionMode')} checked={model.productionMode}>
                    <FormattedMessage id='intl-msg:deviceProdMode'/>
                </Checkbox>
            </div>

            <div className='form-group form-group-vehicle-speed-bypass'>
                <Checkbox onClick={handleCheckboxChange('vehicleSpeedBypass')} checked={model.vehicleSpeedBypass}>
                    <FormattedMessage id='intl-msg:bypassVehicleSpeed'/>
                </Checkbox>
            </div>

            <div className='form-group form-group-description'>
                <label className='control-label'>
                    <FormattedMessage id='intl-msg:description'/>
                </label>
                <FormattedMessage id='intl-msg:description.prompt'>
                    {placeholder =>
                        <input
                            type='text'
                            ref={descriptionInput}
                            className='form-control'
                            value={model.description}
                            placeholder={placeholder}
                            onChange={handleDescriptionChange}
                        />
                    }
                </FormattedMessage>
            </div>
        </form>
    );
};

DeviceEditorForm.defaultProps = {
    model: {},
    groups: [],
    changeControlDeviceEditor: () => {},
};

DeviceEditorForm.propTypes = {
    model: PropTypes.shape({
        isNew: PropTypes.bool,
        serialNumber: PropTypes.string,
        isValidSerialNumber: PropTypes.bool,
        type: PropTypes.string,
        deviceGroups: PropTypes.arrayOf(PropTypes.string),
        inCustomerFleet: PropTypes.bool,
        updatesActive: PropTypes.bool,
        updateBlockedReason: PropTypes.string,
        testReleasesActive: PropTypes.bool,
        replaced: PropTypes.bool,
        productionMode: PropTypes.bool,
        vehicleSpeedBypass: PropTypes.bool,
        description: PropTypes.string,
    }),
    groups: PropTypes.arrayOf(Group),
    changeControlDeviceEditor: PropTypes.func,
};

export default DeviceEditorForm;
